import { useState } from "react";
import { ChevronDown } from "lucide-react";
import MetricCard from "../components/MetricCard";
import SectionIntro from "../components/SectionIntro";
import styles from "./StatsPage.module.css";
import { useLanguage } from "../LanguageContext";
import { useStats } from "../usePlatformData";
import feePoolIcon from "../assets/stats/fee-pool.svg";
import feeReturnedIcon from "../assets/stats/fee-returned.svg";
import feePlatformIcon from "../assets/stats/fee-platform.svg";

// Where the activation fee goes; each key maps to stats_fee_<key> / stats_fee_<key>_desc
const FEE_SPLIT = [
  { key: "pool", icon: feePoolIcon },
  { key: "returned", icon: feeReturnedIcon },
  { key: "platform", icon: feePlatformIcon },
];

const LANG_LOCALE = { EN: "en-US", ES: "es-ES", PT: "pt-BR", FR: "fr-FR", AR: "ar-EG" };
// Months arrive as 1–12; capitalised, since Spanish, Portuguese and French give "ene", "fév"
function monthLabel(month, locale) {
  const label = new Intl.DateTimeFormat(locale, { month: "short" }).format(new Date(2000, month - 1, 1));
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}

const formatCount = (value) => value.toLocaleString("en-US");

// "—" until the figure loads, or while the platform doesn't track it yet
const formatFigure = (value, prefix = "") => (value == null ? "—" : `${prefix}${formatCount(Math.round(value))}`);

function formatUsdK(value) {
  if (value === 0) return "0";
  return value < 1000 ? `$${value}` : `$${Math.round(value / 1000)}K`;
}

// Smallest 1/2/5 × 10ⁿ step that fits the tallest bar in five gridlines
function niceStep(maxValue) {
  for (let magnitude = 1; ; magnitude *= 10) {
    for (const factor of [1, 2, 5]) {
      if (maxValue <= factor * magnitude * 5) return factor * magnitude;
    }
  }
}

function buildBarTicks(values) {
  const stepSize = niceStep(Math.max(...values));
  const max = Math.max(stepSize, Math.ceil(Math.max(...values) / stepSize) * stepSize);
  const tickCount = max / stepSize;
  return {
    max,
    ticks: Array.from({ length: tickCount + 1 }, (_, index) => max - index * stepSize),
  };
}

// A full year of bars needs tighter spacing than the six-month view
const barSeriesClass = (data) => `${styles.barSeries} ${data.length > 6 ? styles.barSeriesDense : ""}`;

function YearSelect({ value, years, onChange }) {
  const { t } = useLanguage();
  return (
    <label className={`${styles.selectWrap} d-inline-flex align-items-center`}>
      <select
        className={styles.yearSelect}
        value={value}
        aria-label={t("stats_year_filter")}
        onChange={(event) => onChange(event.target.value)}
      >
        {years.map((year) => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>
      <ChevronDown className={styles.selectIcon} aria-hidden="true" />
    </label>
  );
}

// Single-series bar chart; hovering or focusing a bar shows its value
function BarChart({ data, formatTick, formatValue, locale, label }) {
  const scale = buildBarTicks(data.map((item) => item.value));
  return (
    <div className={styles.barChart} role="img" aria-label={label}>
      <div className={styles.barGrid} aria-hidden="true">
        {scale.ticks.map((tick) => (
          <div key={tick} className={styles.barGridRow}>
            <span>{formatTick(tick)}</span>
            <div />
          </div>
        ))}
      </div>

      <div className={barSeriesClass(data)}>
        {data.map((item) => (
          <div key={item.month} className={styles.barItem}>
            <div
              className={styles.bar}
              style={{ height: `${(item.value / scale.max) * 100}%` }}
              tabIndex={0}
              aria-label={`${monthLabel(item.month, locale)}: ${formatValue(item.value)}`}
            >
              <span className={styles.barTooltip} aria-hidden="true">{formatValue(item.value)}</span>
            </div>
            <span className={styles.barLabel}>{monthLabel(item.month, locale)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Grouped bars (daily and max drawdown breaches per month); each bar shows its count on
// hover or focus, like the single-series charts
function BreachChart({ data, locale, label, dailyLabel, maxLabel }) {
  const scale = buildBarTicks(data.flatMap((item) => [item.daily, item.max]));
  return (
    <div className={styles.barChart} role="img" aria-label={label}>
      <div className={styles.barGrid} aria-hidden="true">
        {scale.ticks.map((tick) => (
          <div key={tick} className={styles.barGridRow}>
            <span>{tick}</span>
            <div />
          </div>
        ))}
      </div>

      <div className={barSeriesClass(data)}>
        {data.map((item) => (
          <div key={item.month} className={styles.barItem}>
            <div className={styles.groupedBars}>
              {[
                { key: "daily", value: item.daily, series: dailyLabel, tone: styles.dailyBar },
                { key: "max", value: item.max, series: maxLabel, tone: styles.maxBar },
              ].map((bar) => (
                <div
                  key={bar.key}
                  className={`${styles.groupedBar} ${bar.tone}`}
                  style={{ height: `${(bar.value / scale.max) * 100}%` }}
                  tabIndex={0}
                  aria-label={`${monthLabel(item.month, locale)}, ${bar.series}: ${bar.value}`}
                >
                  <span className={styles.barTooltip} aria-hidden="true">{bar.value}</span>
                </div>
              ))}
            </div>
            <span className={styles.barLabel}>{monthLabel(item.month, locale)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// A chart with its year picker. `series` maps year → monthly points; while it's loading,
// failed or empty the chart area shows a message instead.
function YearlyChart({ series, status, emptyMessage, legend, children }) {
  const { t } = useLanguage();
  const [year, setYear] = useState(null);
  const years = series ? Object.keys(series).sort().reverse() : [];
  const selected = series?.[year] ? year : years[0];

  let message = null;
  if (status === "loading") message = t("stats_loading");
  else if (status === "error") message = t("stats_load_error");
  else if (!selected) message = emptyMessage;

  return (
    <>
      <div className={styles.chartHeaderRow}>
        {selected && <YearSelect value={selected} years={years} onChange={setYear} />}
        {legend}
      </div>
      {message ? (
        <p className={styles.chartMessage} role="status">{message}</p>
      ) : (
        children(series[selected])
      )}
    </>
  );
}

export default function StatsPage() {
  const { t, langCode } = useLanguage();
  const locale = LANG_LOCALE[langCode] ?? "en-US";
  const { data, status } = useStats();
  const overview = data?.overview;

  const overviewCards = [
    { label: t("stats_free_accounts"), info: t("stats_tip_free"), value: formatFigure(overview?.freeAccounts) },
    { label: t("stats_qualified"), info: t("stats_tip_qualified"), value: formatFigure(overview?.qualified) },
    { label: t("stats_activated"), info: t("stats_tip_activated"), value: formatFigure(overview?.activated) },
    { label: t("stats_usdc_paid"), info: t("stats_tip_usdc_paid"), value: formatFigure(overview?.usdcPaid, "$") },
  ];

  // DOM order is column-first so mobile stacks max loss → withdrawal → daily → rate
  const rules = [
    { label: t("stats_rule_max_loss"), value: "20%", desc: t("stats_rule_max_loss_desc"), tone: "sage" },
    { label: t("stats_rule_withdraw"), value: "20%", desc: t("stats_rule_withdraw_desc"), tone: "sage" },
    { label: t("stats_rule_daily"), value: "5%", desc: t("stats_rule_daily_desc"), tone: "gold" },
    { label: t("stats_rule_rate"), value: "1 PRP = 0.50 USDC", desc: t("stats_rule_rate_desc"), tone: "sage", accent: true },
  ];

  const transparency = [
    { title: t("stats_target_title"), desc: t("stats_target_desc") },
    { title: t("stats_rate_title"), desc: t("stats_rate_desc") },
    { title: t("stats_limits_title"), desc: t("stats_limits_desc") },
  ];

  return (
    <section className={styles.page}>
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>{t("stats_title")}</h1>
        <p className={styles.heroCopy}>{t("stats_subtitle")}</p>
      </section>

      <section className={styles.section}>
        <p className={styles.sectionLabel}>{t("stats_section_overview")}</p>
        <div className={styles.overviewGrid}>
          {overviewCards.map((card) => (
            <MetricCard key={card.label} {...card} />
          ))}
        </div>
      </section>

      <section className={`${styles.section} ${styles.analyticsGrid}`}>
        <div className={styles.stackSection}>
          <SectionIntro
            label={t("stats_history_label")}
            title={t("stats_qual_title")}
            description={t("stats_qual_desc")}
          />
          <YearlyChart series={data?.qualifications} status={status} emptyMessage={t("stats_no_data")}>
            {(points) => (
              <BarChart
                data={points}
                formatTick={String}
                formatValue={formatCount}
                locale={locale}
                label={t("stats_qual_title")}
              />
            )}
          </YearlyChart>
        </div>

        <div className={`${styles.stackSection} ${styles.feeColumn}`}>
          <SectionIntro title={t("stats_fee_title")} />
          <article className={styles.feeCard}>
            <div className={styles.feeCardText}>
              <p className={`${styles.cardLabel} ${styles.cardLabelLarge}`}>{t("stats_fee_onetime")}</p>
              <strong className={styles.feeHeadline}>{t("stats_fee_onetime_desc")}</strong>
            </div>
            <span className={styles.feeBadge}>{t("stats_fee_badge")}</span>
          </article>
          <div className={styles.feeSplit}>
            {FEE_SPLIT.map(({ key, icon }) => (
              <article key={key} className={styles.feeSplitCard}>
                <div className={styles.feeSplitHead}>
                  <img src={icon} width="24" height="24" alt="" className={styles.feeSplitIcon} />
                  <p className={styles.cardLabel}>{t(`stats_fee_${key}`)}</p>
                </div>
                <strong className={styles.feeSplitDesc}>{t(`stats_fee_${key}_desc`)}</strong>
              </article>
            ))}
          </div>
          <p className={styles.feeNote}>{t("stats_fee_note")}</p>
        </div>
      </section>

      <section className={`${styles.section} ${styles.analyticsGrid}`}>
        <div className={styles.stackSection}>
          <SectionIntro
            label={t("stats_redemptions_label")}
            title={t("stats_redemptions_title")}
            description={t("stats_redemptions_desc")}
          />
          <YearlyChart series={data?.redemptions} status={status} emptyMessage={t("stats_no_payouts")}>
            {(points) => (
              <BarChart
                data={points}
                formatTick={formatUsdK}
                formatValue={(value) => `$${formatCount(value)}`}
                locale={locale}
                label={t("stats_redemptions_title")}
              />
            )}
          </YearlyChart>
        </div>

        <div className={styles.stackSection}>
          <SectionIntro
            label={t("stats_outcomes_label")}
            title={t("stats_breaches_title")}
            description={t("stats_breaches_desc")}
          />
          <YearlyChart
            series={data?.breaches}
            status={status}
            emptyMessage={t("stats_no_data")}
            legend={
              <div className={styles.legend}>
                <span className={styles.legendDailyItem}>
                  <i className={`${styles.legendDot} ${styles.legendDaily}`} />
                  {t("stats_daily_dd_breach")}
                </span>
                <span className={styles.legendMaxItem}>
                  <i className={`${styles.legendDot} ${styles.legendMax}`} />
                  {t("stats_max_dd_breach")}
                </span>
              </div>
            }
          >
            {(points) => (
              <BreachChart
                data={points}
                locale={locale}
                label={t("stats_breaches_title")}
                dailyLabel={t("stats_daily_dd_breach")}
                maxLabel={t("stats_max_dd_breach")}
              />
            )}
          </YearlyChart>
        </div>
      </section>

      <section className={styles.section}>
        <p className={styles.sectionLabel}>{t("stats_rules_label")}</p>
        <div className={styles.rulesGrid}>
          {rules.map((rule) => (
            <article
              key={rule.label}
              className={`${styles.ruleCard} ${rule.tone === "gold" ? styles.ruleGold : ""}`}
            >
              <p className={styles.cardLabel}>{rule.label}</p>
              <strong className={`${styles.ruleValue} ${rule.accent ? styles.ruleValueAccent : ""}`}>
                {rule.value}
              </strong>
              <p className={styles.ruleDesc}>{rule.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={`${styles.section} ${styles.transparencyCard}`}>
        <p className={styles.sectionLabel}>{t("stats_transparency_label")}</p>
        <h2 className={styles.transparencyTitle}>{t("stats_transparency_title")}</h2>
        <p className={styles.transparencyDesc}>{t("stats_transparency_desc")}</p>
        <dl className={styles.transparencyGrid}>
          {transparency.map((item) => (
            <div key={item.title}>
              <dt>{item.title}</dt>
              <dd>{item.desc}</dd>
            </div>
          ))}
        </dl>
      </section>
    </section>
  );
}
